import { render } from "preact";
import { useRef, useState, useEffect } from "preact/hooks";
import { encrypt } from "../encrypt";
import { LazyCss } from "../components/css";

import markdownQuestions from '../assessment-questions.txt'

const ANSWERS_KEY = "soc2-assessment-answers"

function parseQuestions() {
  // generate structured questions from Markdown
  const questions = []
  let category = undefined
  for (let line of markdownQuestions.split("\n")) {
    line = line.trim()
    if (line === "") {
      continue
    } else if (line.startsWith("## ")) {
      const keyMatch = line.match(/\{#(\S+)\}/)
      if (!keyMatch) throw new Error(`No key set for ${line}`)
      questions.push({
        title: line.slice(3).replace(keyMatch[0],''),
        key: keyMatch[1],
        type: "textarea",
        category
      })
    } else if (line.startsWith("# ")) {
      const title = line.slice(2)
      category = title
      questions.push({
        title,
        type: "header",
      })
    }
  }
  return questions;
}

function getSavedAnswers() {
  const serializedAnswers = localStorage.getItem(ANSWERS_KEY)
  if (serializedAnswers) {
    try {
      const parsedAnswers = JSON.parse(serializedAnswers)
      return parsedAnswers
    } catch (err) {
      console.warn("Error loading saved answers", err)
      return {}
    }
  }
  return {}
}

const AssessmentPage = () => {
  const [status, setStatus] = useState(undefined);
  const [currentAnswers, setCurrentAnswers] = useState(getSavedAnswers())
  const form = useRef(undefined)

  useEffect(() => {
    const serializedAnswers = JSON.stringify(currentAnswers)
    localStorage.setItem(ANSWERS_KEY, serializedAnswers)
  }, [currentAnswers])

  function toJSON(target) {
    const formData = new FormData(target);
    const formJson = {};
    for (const [k, v] of formData.entries()) {
      if (formJson[k]) throw new Error(`Key ${k} is already present`)
      formJson[k] = v;
    }
    return formJson
  }

  function updateAnswer(key, answer) {
    const newAnswer = { [key]: answer }
    setCurrentAnswers({ ...currentAnswers, ...newAnswer })
  }

  async function encryptAndMail(evt) {
    evt.preventDefault();
    const formJson = toJSON(evt.currentTarget)
    const encrypted = await encrypt(JSON.stringify(formJson));
    window.location.href = `mailto:hello@monorail.tech?subject=${encodeURIComponent("SOC 2 assessment responses")}&body=${encodeURIComponent(`Hi there!\n\nHere's my form response: \n\n ${encrypted}\n\n`)}`;
    setStatus("sent");
    // TODO clear here? Or prompt to download markdown?
  }

  const questions = parseQuestions()

  function download() {
    const questions = parseQuestions()
    // fill in any answers so far
    const answers = JSON.parse(JSON.stringify(currentAnswers));
    for (const [key, answer] of Object.entries(answers)) {
      const q = questions.find(q => q.key === key)
      if (!q) throw new Error(`Couldn't find question with key ${key}`)
      q.answer = answer
    }
    // console.log("questions with answers:", questions)
    const lines = []
    for (const q of questions) {
      if (q.type === "header") {
        lines.push(`# ${q.title}\n`)
      } else {
        lines.push(`## ${q.title.trim()} {#${q.key}}\n`)
        if (q.answer) {
          lines.push(`${q.answer}\n`)
        }
      }
    }
    const markdown = lines.join("\n").trim()
    const dataURI = `data:text/plain;charset=utf-8,${encodeURIComponent(markdown)}`

    const a = document.createElement('a')
    a.href = dataURI
    a.download = `SOC 2 assessment answers for Monorail ${new Date().toISOString().split("T")[0]}.md`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  function clearForm() {
    localStorage.removeItem(ANSWERS_KEY)
    setCurrentAnswers({})
  }

  return (
    <>
      <LazyCss url="https://fonts.googleapis.com/css2?family=Atkinson+Hyperlegible:ital,wght@0,400;0,700;1,400;1,700&family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap" />
      <LazyCss url="https://fonts.googleapis.com/css2?family=Inter:ital,wght@0,300;0,400;0,500;1,300;1,400;1,500&display=swap" />

      <div class="bg-monorail min-h-full p-2 md:p-8 monorail-sans">
        <section class="bg-gray-50 max-w-3xl m-auto p-6 shadow-lg rounded-sm">
          <h1 class="text-2xl md:text-3xl font-semibold monorail-header">
            Monorail Technology{" "}
            <span class="block md:inline font-light">SOC 2 Initial Assessment</span>
          </h1>
          {status === "sent" && (
            <>
              <p class="mt-6 mb-2">
                Thanks! Go ahead and send the email draft that should have popped
                up, and we'll get back to you shortly.
              </p>
              <div class="btn btn-sm mt-4 mr-2 btn-soft" onClick={download}>Download as Markdown</div>
              <div class="btn btn-sm mt-4 btn-soft" onClick={clearForm}>Clear form</div>
            </>
          )}
          {status === undefined && (
            <>
              <p class="mt-2 text-sm sm:text-base text-gray-600">
                Please answer the following questions about your
                current security and compliance-related practices.
              </p>
              <p class="mt-2 text-sm sm:text-base text-gray-600">
                There are no wrong answers, just answer accurately. These
                are only for us to discuss as a starting point, and won't be shared with
                anyone outside of Monorail and your organization.
              </p>
              <p class="mt-2 text-sm sm:text-base text-gray-600">
                When you're done, this form will generate an email draft with an
                encrypted version of your responses that can only be read by
                Monorail.
              </p>
              <p class="mt-2 text-sm sm:text-base text-gray-600">
                If you like, you can download these questions (and any answers you've entered so far)
                as a Markdown file. Answers will be saved locally in your browser prior to submission,
                as well, so you won't lose your work.
              </p>
              <div class="btn btn-sm mt-4 mr-2 btn-soft" onClick={download}>Download as Markdown</div>
              <div class="btn btn-sm mt-4 btn-soft" onClick={clearForm}>Clear form</div>
              <form onSubmit={encryptAndMail} ref={form}>
                {questions.map((q) => (
                  <fieldset class="fieldset mt-4 md:mt-6">
                    {q.type === "header" &&
                      <h2 class="text-xl font-medium mt-1 md:mt-2">{q.title}</h2>
                    }

                    {q.type !== "header" && <legend class="text-sm md:text-base font-medium mb-1">
                      {q.title}
                    </legend>}

                    {q.type === "textarea" && (
                      <textarea
                        onInput={(evt) => updateAnswer(q.key, evt.currentTarget.value)}
                        class="textarea w-full validator text-sm md:text-base"
                        name={q.key}
                        required
                      >{currentAnswers[q.key]}</textarea>
                    )}
                  </fieldset>
                ))}
                <button class="btn btn-active btn-primary mt-6 mb-2 w-full sm:w-auto text-base">
                  <svg
                    aria-label="Email icon"
                    width="16"
                    height="16"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                  >
                    <g
                      stroke-linejoin="round"
                      stroke-linecap="round"
                      stroke-width="2"
                      fill="none"
                      stroke="white"
                    >
                      <rect width="20" height="16" x="2" y="4" rx="2"></rect>
                      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
                    </g>
                  </svg>
                  Send to Monorail &rarr;
                </button>
                <div class="btn md:ml-2 btn-soft md:mt-6 mb-2 w-full sm:w-auto text-base font-normal" onClick={download}>Download as Markdown</div>
              </form>
            </>
          )}
        </section>
      </div>
    </>
  );
};

render(<AssessmentPage />, document.querySelector("main"));
